import React, { useMemo } from "react";
import { Text, Group } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";
import { LookupDictionary, LookupDictionaryItem } from "@/utils/api";
import { formatDateTime } from "@/utils/dateHelpers";
import { OPERATION_RIGHTS } from "@/config";

export interface LookupDictionaryItemsTableProps {
  /** Array of lookup dictionary items to display */
  lookupDictionaryItems: LookupDictionaryItem[];

  lookupDictionary?: LookupDictionary | null;

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditItem?: (item: LookupDictionaryItem) => void;

  /** Callback when remove action is clicked */
  onRemoveItem?: (item: LookupDictionaryItem) => void;

  /** ID of item currently being removed */
  removingItemId?: string | null;

  /** ID of item just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const LookupDictionaryItemsTable: React.FC<
  LookupDictionaryItemsTableProps
> = ({
  lookupDictionaryItems,
  lookupDictionary,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditItem,
  onRemoveItem,
  removingItemId,
  editedItemId,
  size = "md",
}) => {
  const canEdit = lookupDictionary?.rights?.includes(OPERATION_RIGHTS.UPDATE);
  const canRemove = lookupDictionary?.rights?.includes(OPERATION_RIGHTS.REMOVE);

  const columns: AppTableColumn<LookupDictionaryItem>[] = useMemo(
    () => [
      {
        key: "checkValue",
        label: "Value",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "—"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "filter1",
        label: "Filter 1",
        sortable: true,
        width: 150,
        minWidth: 120,
        render: (value) => (
          <Text size="sm" c={value ? undefined : "dimmed"}>
            {value || "—"}
          </Text>
        ),
      },
      {
        key: "filter2",
        label: "Filter 2",
        sortable: true,
        width: 150,
        minWidth: 120,
        render: (value) => (
          <Text size="sm" c={value ? undefined : "dimmed"}>
            {value || "—"}
          </Text>
        ),
      },
      {
        key: "expiresAt",
        label: "Expires",
        sortable: true,
        width: 150,
        minWidth: 130,
        align: "left",
        render: (value) => {
          if (!value) {
            return (
              <Text size="sm" c="dimmed">
                Never
              </Text>
            );
          }

          const expiresDate = new Date(value);
          const isExpired = expiresDate < new Date();

          return (
            <Text
              size="sm"
              c={isExpired ? "red" : undefined}
              dangerouslySetInnerHTML={{
                __html: formatDateTime(value, "datetime2"),
              }}
            />
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => <StatusBadge status={value} />,
      },
      {
        key: "actions",
        label: "",
        width: 120,
        minWidth: 120,
        align: "left",
        render: (_, record: LookupDictionaryItem) => {
          const isRemoving = removingItemId === record.id;

          return (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              <RecordActions
                context={record}
                onEditClick={onEditItem}
                onRemoveClick={onRemoveItem}
                removeInProgress={isRemoving}
                editDisabled={!canEdit}
                removeDisabled={!canRemove}
              />
            </Group>
          );
        },
      },
    ],
    [onEditItem, onRemoveItem, removingItemId]
  );

  return (
    <AppTable
      data={lookupDictionaryItems}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(item) => item?.id || ""}
      highlightRow={(item) => item?.id === editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
